import React from 'react'

import { useStatus } from '@sketch-hq/sketch-web-renderer'
import { useForTablet } from '@sketch/components'
import { IS_EMBEDDED } from '@sketch/constants'

import { useCanvasZoom } from '../../../../components/WebRendererCanvas/hooks'
import { usePlaceDraftAnnotation } from '../../../annotations/hooks'
import { DocumentMobileToolbar, ToolboxItem } from '../DocumentMobileToolbar'

interface CanvasMobileToolbarProps {
  /**
   * Which controls to offer. A page canvas exposes the document-wide ones -
   * info and search; a focus view offers only what applies to the entity on
   * screen, matching the header it already has.
   */
  items: ToolboxItem[]
  hideSearch?: boolean
}

/**
 * The toolbar along the bottom of the canvas on a phone.
 *
 * Shows itself only where there is room: never when embedded, never above the
 * tablet breakpoint. Those were guards at each call site, which is how the
 * Section focus view ended up with no toolbar at all.
 *
 * Zoom, draft-annotation state and canvas readiness all come from the canvas
 * itself, so a view only has to say which controls belong in its context.
 */
export function CanvasMobileToolbar({
  items,
  hideSearch,
}: CanvasMobileToolbarProps) {
  const isTabletAndBigger = useForTablet()
  const canvasStatus = useStatus()
  const [zoom, setZoom] = useCanvasZoom()
  const [placeDraftAnnotation, setPlaceDraftAnnotation] =
    usePlaceDraftAnnotation() || []

  if (IS_EMBEDDED || isTabletAndBigger) {
    return null
  }

  return (
    <DocumentMobileToolbar
      items={items}
      zoom={zoom}
      setZoom={setZoom}
      isDraftAnnotationEnabled={placeDraftAnnotation}
      setPlaceDraftAnnotation={setPlaceDraftAnnotation}
      disableAddAnnotationButton={canvasStatus?.type !== 'READY'}
      hideSearch={hideSearch}
    />
  )
}

/** The page canvas: everything, including the document-wide controls. */
export const PAGE_CANVAS_TOOLBAR_ITEMS: ToolboxItem[] = [
  'doc-info',
  'search',
  'zoom',
  'add-comment',
  'versions',
  'comments',
]

/**
 * A focus view: no document-wide search or info, mirroring the breadcrumb
 * headers these views use in place of the page header.
 */
export const FOCUS_VIEW_TOOLBAR_ITEMS: ToolboxItem[] = [
  'zoom',
  'add-comment',
  'comments',
  'versions',
]
